import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { BinScatterMultiSelectResult } from 'libs/end-to-end/src/bin-scatter';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import Alert from '@mui/material/Alert';
import { BinScatterChart } from 'apps/sigma2-ui/src/components/BinScatterChart';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { useExplainImage } from 'apps/sigma2-ui/src/lib/hooks/useExplainImage';
import { ExplanationBlock } from 'apps/sigma2-ui/src/components/ExplanationBlock';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { capitalize } from 'lodash';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { BinScatterMultiSelectRequest } from 'libs/end-to-end/src/custom-viz/BinScatterMultiSelectTypes';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { getLevelKeysFromBinScatterMultiSelectResult } from 'libs/end-to-end/src/bin-scatter-multi-select-utils';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { getMetricFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getDimensionFilterValue } from 'libs/end-to-end/src/progress-page-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { getBinScatterMultiSelectPrimaryFilterSelections } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { LevelDefinitionButton } from 'apps/sigma2-ui/src/components/LevelDefinitionButton';
import { uniqueSet } from 'libs/end-to-end/src/arrays';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { InterpretingTrendLinesSection } from 'apps/sigma2-ui/src/components/InterpretingTrendLinesSection';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { getFocusAreaDefinitionForMyObservation } from 'libs/end-to-end/src/my-observation-utils';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';

export const BinScatterMultiSelectFullInterface = (props: {
  focusArea: MyFocusAreaDefinition;
  observation: ObservationLocationType;
  myObservation: MyObservation;
  organizationId: string;
}) => {
  const { focusArea, organizationId, myObservation } = props;

  const { myUser, userAuthenticatedApiCall } = useUser();
  const language = useLanguage();

  const context = useCustomViz();
  const { doViz } = context;

  const shareLinkRawData = useShareLinkSearchParameter();

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): { customVizRequest: BinScatterMultiSelectRequest | undefined } => {
      const metric = getMetricFromPrimaryFilterValues(primaryFilterSelections);
      const dimension = getDimensionFilterValue(primaryFilterSelections);

      if (!metric || !dimension) {
        return { customVizRequest: undefined };
      }

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: BinScatterMultiSelectRequest = {
        customVizId: 'binScatterMultiSelect',
        inputs: {
          extras: {
            filters,
            metric,
            dimension,
            focusArea: focusArea.focusArea,
          },
          dropdowns: {},
        },
        observationLocationType: myObservation.type,
        organizationId,
      };

      return {
        customVizRequest,
      };
    },
    [focusArea.focusArea, myObservation.type, organizationId],
  );

  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      OnApplyFnArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (
        !customVizRequest ||
        customVizRequest.customVizId !== 'binScatterMultiSelect'
      ) {
        return;
      }

      doViz(customVizRequest);
    },
    [doViz, getCustomVizRequest],
  );

  const onQueryProgress = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [onAnalyzeWithParams, shareLinkRawData],
  );

  const filterPayload = useFilters({
    feature: 'binScatterMultiSelect',
    focusArea: focusArea.focusArea,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onQueryProgress,
    getCustomVizRequest,
    analysisName: undefined,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (
        !myObservation ||
        !request ||
        request.customVizId !== 'binScatterMultiSelect'
      ) {
        return;
      }

      const filters = request.inputs.extras.filters;

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'binScatterMultiSelect',
          focusArea: null,
          customVizFilters: filters,
          focusAreas: myObservation.focusAreas,
          filterSetDefinitionsWithValues:
            myObservation.filterSetDefinitionsWithValues,
          dimensionDefinitions: myObservation.dimensions,
          pipelineAnalysisConfiguration:
            myObservation.pipelineAnalysisConfiguration,
          analysisName: undefined,
        });

      const primaryFilterSelections =
        getBinScatterMultiSelectPrimaryFilterSelections({
          request,
        });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, myObservation, onAnalyzeWithParams],
  );

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: BinScatterMultiSelectRequest,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  const imageRef = React.useRef<HTMLInputElement | null>(null);

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  return (
    <div ref={imageRef} id="bin-scatter-multi-select">
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />

      <Spacing size={'xl'} direction={'vertical'}>
        <S2Typography
          translatedContent={language.groupTrends()}
          variant={'headline-3'}
        />

        <HorizontalFilterBar
          items={filterPayload.items}
          slotRight={
            <DataRefreshIndicator
              status={context.status}
              resultCreatedAt={context.resultCreatedAt}
            />
          }
        />

        <Card className={'relative min-h-96 flex'} paddingClassName={'p-0'}>
          <BinScatterMultiSelectCard
            customVizRequest={lastCustomVizRequest}
            result={context.result}
            status={context.status}
          />
        </Card>
      </Spacing>
    </div>
  );
};

// eslint-disable-next-line complexity
export const BinScatterMultiSelectCard = (props: {
  customVizRequest: BinScatterMultiSelectRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
}) => {
  const { customVizRequest, result, status } = props;

  const { myObservation } = useObservation();

  const [showTrendLinesInformationModal, setShowTrendLinesInformationModal] =
    useState(false);

  const closeTrendLinesInformationModal = () => {
    setShowTrendLinesInformationModal(false);
  };

  const [activeLevelKeys, setActiveLevelKeys] = useState<string[]>([]);

  const [keyTakeawaysOpen, setKeyTakeawaysOpen] = useState(false);
  const language = useLanguage();

  const {
    getDimensionByColumnHeaderValue,
    allPrivilegedLevels,
    getLevelByColumnValue,
  } = useDimensionConfig();
  const { getMetricByName } = useMetricConfig();

  const { readyToDisplayDynamicContent, isLoading, data, hasFailed } =
    useMemo(() => {
      return parseCustomVizResult({
        result: result,
        status: status,
        ResultZodType: BinScatterMultiSelectResult,
      });
    }, [result, status]);

  const submittedDimension = useMemo(() => {
    return customVizRequest?.inputs.extras.dimension;
  }, [customVizRequest?.inputs.extras.dimension]);

  const submittedMetric = useMemo(() => {
    return customVizRequest?.inputs.extras.metric;
  }, [customVizRequest?.inputs.extras.metric]);

  const submittedFocusAreaDefinition = useMemo(() => {
    const focusArea = customVizRequest?.inputs.extras.focusArea;
    return focusArea
      ? getFocusAreaDefinitionForMyObservation({ myObservation, focusArea })
      : null;
  }, [customVizRequest?.inputs.extras.focusArea, myObservation]);

  const submittedMetricDefinition = useMemo(() => {
    return submittedFocusAreaDefinition && submittedMetric
      ? getMetricByName({
          focusArea: submittedFocusAreaDefinition.focusArea,
          metricName: submittedMetric,
          myObservation,
        })
      : null;
  }, [
    submittedFocusAreaDefinition,
    submittedMetric,
    getMetricByName,
    myObservation,
  ]);

  const submittedDimensionDefinition = useMemo(() => {
    return submittedDimension
      ? getDimensionByColumnHeaderValue(submittedDimension)
      : null;
  }, [getDimensionByColumnHeaderValue, submittedDimension]);

  const levelKeys = getLevelKeysFromBinScatterMultiSelectResult({
    result: data,
  });

  const pageSectionWidth = 1264;
  const keyTakeawayOffset = 158;
  const chartMaxWidth = pageSectionWidth - keyTakeawayOffset;

  useEffect(() => {
    if (!data) {
      return;
    }

    setActiveLevelKeys(
      getLevelKeysFromBinScatterMultiSelectResult({ result: data }),
    );
  }, [chartMaxWidth, data]);

  const imageRef = React.useRef<HTMLInputElement | null>(null);
  const explanationHook = useExplainImage({
    ref: imageRef,
    key: JSON.stringify(customVizRequest),
    autoExplain:
      data !== null &&
      readyToDisplayDynamicContent &&
      Boolean(submittedDimension),
  });

  if (isLoading) {
    return (
      <div className="z-cover flex items-center justify-center w-full h-full flex-1 place-self-center">
        <CustomVizCircularSpinner />
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      {!isLoading && hasFailed && (
        <div className="z-cover flex items-center justify-center w-full h-full min-h-96">
          <Alert severity="error">
            There&apos;s not enough data to render, please adjust your filters
          </Alert>
        </div>
      )}
      {!isLoading &&
        readyToDisplayDynamicContent &&
        submittedDimension &&
        submittedMetricDefinition &&
        submittedFocusAreaDefinition && (
          <>
            {explanationHook.explanation && (
              <ExplanationBlock
                explanation={explanationHook.explanation}
                titleTranslatedContent={language.binScatterMultiSelectAIExplanationHeading(
                  {
                    dimension: submittedDimensionDefinition?.displayName ?? '',
                    xAxis: capitalize(
                      language.scatterChartXLabel({
                        metricDefinition: submittedMetricDefinition,
                      }),
                    ),
                    yAxis: capitalize(
                      language.scatterChartYLabel({
                        focusAreaDefinition: submittedFocusAreaDefinition,
                        metricDefinition: submittedMetricDefinition,
                      }),
                    ),
                  },
                )}
              />
            )}

            <Spacing direction="horizontal" size="" className={'p-6 pb-0'}>
              <Spacing direction="vertical" size="md">
                {submittedMetricDefinition && (
                  <S2Typography
                    variant={'body-6'}
                    translatedContent={language.focusAreaGroupTrendsHeading({
                      metricDefinition: submittedMetricDefinition,
                    })}
                  />
                )}

                <button
                  aria-label="Group Trends Modal"
                  className="text-secondary text-sm text-left w-fit flex flex-row items-center"
                  onClick={() => setShowTrendLinesInformationModal(true)}
                  data-qa="interpretingTrendLinesSectionInfo"
                >
                  {language.focusAreaGroupTrendsSubheading()}
                  <IconChevronRightSvg svg={{ className: 'ml-1 h-4 w-4' }} />
                </button>
              </Spacing>
            </Spacing>

            <Spacing
              direction="horizontal"
              size="gap-2"
              className="p-6 flex-wrap"
            >
              {levelKeys
                .sort((k1, k2) => {
                  const k1baseline = allPrivilegedLevels
                    .map((level) => level.columnValue)
                    .includes(k1);
                  const k2baseline = allPrivilegedLevels
                    .map((level) => level.columnValue)
                    .includes(k2);

                  if (k1baseline && !k2baseline) {
                    return -1;
                  }

                  if (!k1baseline && k2baseline) {
                    return 1;
                  }

                  return 0;
                })
                .map((levelKey) => {
                  if (!submittedDimension) {
                    return <></>;
                  }
                  const levelDefinition = getLevelByColumnValue(
                    submittedDimension,
                    levelKey,
                  );
                  if (!levelDefinition) {
                    return <></>;
                  } else {
                    return (
                      <LevelDefinitionButton
                        key={levelDefinition.columnValue}
                        icon={true}
                        disabled={allPrivilegedLevels
                          .map((level) => level.columnValue)
                          .includes(levelKey)}
                        selected={activeLevelKeys.includes(levelKey)}
                        levelDefinition={levelDefinition}
                        onSelect={(enabled) => {
                          setActiveLevelKeys((prevState) =>
                            uniqueSet(
                              enabled
                                ? [...prevState, levelKey]
                                : prevState.filter((d) => d !== levelKey),
                            ),
                          );
                        }}
                      />
                    );
                  }
                })}
            </Spacing>

            <div className="relative">
              <div className="p-6" id="binScatterChart">
                <BinScatterChart
                  binScatterMultiSelectOutput={data}
                  focusArea={submittedFocusAreaDefinition}
                  metricDefinition={submittedMetricDefinition}
                  dimension={submittedDimension}
                  activeLevelKeys={activeLevelKeys}
                />
              </div>
            </div>
            <div className={'py-2'}></div>
            {submittedMetricDefinition && myObservation && (
              <Modal
                show={showTrendLinesInformationModal}
                onClose={closeTrendLinesInformationModal}
                title={language.focusAreaIndicatorsHeading()}
              >
                <InterpretingTrendLinesSection
                  metricDefinition={submittedMetricDefinition}
                  calibrationFraming={myObservation?.calibrationFraming}
                />
              </Modal>
            )}
          </>
        )}
    </div>
  );
};
